import { EWSS_GRADES } from "../constants/registerFilters";
import { REGISTER_SEARCH_MODES } from "../constants/registerPreferences";
import { getWordEwssDisplayGrade } from "./ewssGradeDisplay";
import { foldFrenchLigatures } from "./frenchOrthography";

export const normalizeRegisterSearch = (value) =>
  foldFrenchLigatures(String(value || ""))
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, "'")
    .toLowerCase()
    .trim();

function parseDateForSort(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return 0;
  const match = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return 0;
  return new Date(
    Number(match[3]),
    Number(match[2]) - 1,
    Number(match[1])
  ).getTime();
}

function parseTimeForSort(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return 0;
  const parts = timeStr.trim().split(":");
  if (parts.length < 2) return 0;
  const hh = Number(parts[0]);
  const mm = Number(parts[1]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
  return hh * 60 + mm;
}

export function formatCaptureDateDisplay(raw) {
  const value = String(raw || "").trim();
  if (!value) return "";

  const slash = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    return `${slash[1].padStart(2, "0")}/${slash[2].padStart(2, "0")}/${slash[3]}`;
  }

  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return `${iso[3]}/${iso[2]}/${iso[1]}`;
  }

  return "";
}

export function getLastGameEntry(stats) {
  const last5 = stats?.last5Games;
  if (!Array.isArray(last5) || !last5.length) return null;

  return [...last5].sort((a, b) => {
    const dateDiff = parseDateForSort(b.date) - parseDateForSort(a.date);
    if (dateDiff !== 0) return dateDiff;
    return parseTimeForSort(b.time) - parseTimeForSort(a.time);
  })[0];
}

function getWordStats(entry) {
  if (entry?.stats && typeof entry.stats === "object") {
    return entry.stats;
  }

  return {
    gameCount: entry?.gameCount,
    correctCount: entry?.correctCount,
    failCount: entry?.failCount,
    last5Games: entry?.last5Games,
    retention: entry?.retention,
  };
}

export function getWordGameCount(entry) {
  const count = getWordStats(entry)?.gameCount;
  return typeof count === "number" && count >= 0 ? count : 0;
}

export function getWordCorrectCount(entry) {
  const count = getWordStats(entry)?.correctCount;
  return typeof count === "number" && count >= 0 ? count : 0;
}

export function getWordFailCount(entry) {
  const count = getWordStats(entry)?.failCount;
  return typeof count === "number" && count >= 0 ? count : 0;
}

export function isGradeMastered(grade) {
  const gradeIndex = EWSS_GRADES.indexOf(grade);
  const thresholdIndex = EWSS_GRADES.indexOf("B+");
  if (gradeIndex === -1 || thresholdIndex === -1) return false;
  return gradeIndex <= thresholdIndex;
}

export function formatSuccessRatio(correctCount, gameCount) {
  if (!gameCount) return "0";
  const ratio = correctCount / gameCount;
  if (ratio === 0) return "0";
  if (ratio === 1) return "1";
  return ratio.toFixed(2);
}

export function getSuccessRatioTone(correctCount, gameCount) {
  if (!gameCount) return "ratio-0";
  const ratio = correctCount / gameCount;
  if (ratio === 0) return "ratio-0";
  if (ratio < 0.34) return "ratio-1";
  if (ratio < 0.67) return "ratio-2";
  return "ratio-3";
}

export function getGradationBadgeState(entry) {
  const gameCount = getWordGameCount(entry);
  if (gameCount === 0) {
    return { tone: "never", label: "—" };
  }
  if (gameCount <= 2) {
    return { tone: "few", label: "—" };
  }

  const grade = getWordEwssDisplayGrade(entry);
  if (!grade) {
    return { tone: "unmastered", label: "—" };
  }

  return {
    tone: isGradeMastered(grade) ? "mastered" : "unmastered",
    label: grade,
  };
}

/** Plus l’index est élevé, plus la gradation affichée est mauvaise. Sans note : après les lettres. */
export function getGradationSortIndex(entry) {
  const { label } = getGradationBadgeState(entry);
  if (!label || label === "—") return -1;
  const index = EWSS_GRADES.indexOf(label);
  return index === -1 ? -1 : index;
}

export function compareEntriesByGradation(a, b, masteredFirst = false) {
  const rankA = getGradationSortIndex(a);
  const rankB = getGradationSortIndex(b);
  const noneA = rankA < 0;
  const noneB = rankB < 0;
  if (noneA !== noneB) return noneA ? 1 : -1;
  if (rankA !== rankB) return masteredFirst ? rankA - rankB : rankB - rankA;
  return String(a?.mot || "").localeCompare(String(b?.mot || ""), "fr", {
    sensitivity: "base",
  });
}

export function getStratumGradationStats(entries) {
  const list = Array.isArray(entries) ? entries : [];
  let masteredCount = 0;
  let reviewCount = 0;

  for (const entry of list) {
    const { label } = getGradationBadgeState(entry);
    if (!label || label === "—") continue;
    if (isGradeMastered(label)) masteredCount += 1;
    else reviewCount += 1;
  }

  return {
    total: list.length,
    masteredCount,
    reviewCount,
  };
}

export function matchesRegisterSearch(entry, normalizedFilter, searchMode) {
  if (!normalizedFilter) return true;
  const mot = normalizeRegisterSearch(entry?.mot);
  if (searchMode === REGISTER_SEARCH_MODES.STARTS_WITH) {
    return mot.startsWith(normalizedFilter);
  }
  return mot.includes(normalizedFilter);
}
